import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Lock, CreditCard, Server, Eye, KeyRound, FileCheck, Fingerprint,
  ArrowLeft, CheckCircle2, Mail, Phone, Check,
} from "lucide-react";
import { ANTIVIRUS_PLANS, RESELLER } from "@/data/content";

const BADGES = [
  { icon: Lock, label: "256-bit TLS/SSL" },
  { icon: CreditCard, label: "PCI-DSS via Razorpay" },
  { icon: Fingerprint, label: "3D Secure / OTP" },
  { icon: ShieldCheck, label: "HTTPS Everywhere" },
];

const PILLARS = [
  {
    icon: CreditCard,
    title: "Secure Payments",
    text: "All payments are processed by Razorpay, a PCI-DSS Level 1 certified payment gateway. We never see or store your full card number, CVV, or PIN on our servers.",
  },
  {
    icon: Lock,
    title: "Encryption in Transit",
    text: "Every connection to our website and payment flow is protected with 256-bit TLS/SSL encryption, so your data can't be read while it travels over the internet.",
  },
  {
    icon: FileCheck,
    title: "Payment Verification",
    text: "Each transaction is validated using a cryptographic signature check (HMAC-SHA256). Tampered or forged payment responses are automatically rejected.",
  },
  {
    icon: Fingerprint,
    title: "Fraud Prevention",
    text: "Payments are backed by bank-level 3D Secure / OTP authentication through the gateway, reducing unauthorized transactions and card fraud.",
  },
  {
    icon: KeyRound,
    title: "Protected Access",
    text: "Our internal admin area is protected with hashed passwords (bcrypt) and expiring access tokens (JWT). Only authorized staff can view records.",
  },
  {
    icon: Eye,
    title: "Consent & Transparency",
    text: "Before any charge, customers explicitly agree to our Terms & Refund Policy. Each consent is recorded with a timestamp for full transparency.",
  },
  {
    icon: Server,
    title: "Secure Infrastructure",
    text: "Our application runs on managed, monitored cloud infrastructure with isolated environments and regular updates.",
  },
  {
    icon: ShieldCheck,
    title: "Data Minimization",
    text: "We only collect the information needed to deliver your service and process your payment — nothing more.",
  },
];

const COMMITMENTS = [
  "We never store your full card details.",
  "We never sell your personal data.",
  "Payments are handled by a certified gateway (Razorpay).",
  "You are always asked to consent before any charge.",
  "You can request your data or a refund per our policies.",
];

const SECURITY_PLANS = ANTIVIRUS_PLANS;

export default function Security() {
  const navigate = useNavigate();

  const goToCheckout = (plan) => {
    const amount = plan.custom ? "" : plan.price;
    const label = plan.custom ? "Antivirus — Custom Amount" : `Antivirus — ${plan.name}`;
    navigate(`/checkout?plan=${encodeURIComponent(label)}${amount ? `&amount=${amount}` : ""}`);
  };

  return (
    <div className="min-h-screen bg-white text-[#0F172A]" data-testid="security-page">
      {/* Minimal header (no radio content) */}
      <header className="bg-[#26395F] text-white">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="security-brand">
            <span className="h-9 w-9 rounded-lg bg-[#3B82F6] flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-[#0F172A]" strokeWidth={2.5} />
            </span>
            <span className="font-extrabold tracking-tight text-lg">Radio App Consultancy</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors" data-testid="security-back">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#26395F] text-white overflow-hidden relative">
        <div className="max-w-6xl mx-auto px-6 pt-14 pb-20 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#3B82F6]">
                <ShieldCheck className="h-4 w-4" /> Security & Trust
              </span>
              <h1 className="font-display text-4xl sm:text-5xl tracking-tight uppercase mt-5 max-w-3xl leading-[1]">
                Your payments and data are protected
              </h1>
              <p className="text-white/70 mt-5 max-w-2xl leading-relaxed">
                Security is built into everything we do. Here's exactly how we keep your information safe when you
                pay and work with Radio App Consultancy.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="lg:col-span-5"
            >
              <div className="relative rounded-2xl overflow-hidden border border-white/10">
                <img
                  src="https://images.unsplash.com/photo-1614064548237-096f735f344f?auto=format&fit=crop&w=900&q=80"
                  alt="Secure padlock protecting data"
                  className="w-full h-64 object-cover"
                  data-testid="security-hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#26395F] via-[#26395F]/30 to-transparent" />
                <div className="absolute bottom-4 left-4 flex items-center gap-2 text-sm font-semibold">
                  <Lock className="h-4 w-4 text-[#3B82F6]" /> Encrypted & PCI-DSS protected
                </div>
              </div>
            </motion.div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-10 max-w-3xl">
            {BADGES.map((b, i) => (
              <motion.div
                key={b.label}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}
                className="rounded-xl bg-white/5 border border-white/10 px-4 py-4 flex flex-col items-center text-center gap-2"
                data-testid={`security-badge-${i}`}
              >
                <b.icon className="h-6 w-6 text-[#3B82F6]" />
                <span className="text-xs font-semibold text-white/80">{b.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#3B82F6]">How we protect you</p>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight uppercase mt-3">Security measures</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 4) * 0.06 }}
              className="rounded-xl bg-white border border-[#E2E8F0] p-7 hover:border-[#3B82F6] hover:shadow-lg transition-all duration-300"
              data-testid={`security-pillar-${i}`}
            >
              <span className="h-11 w-11 rounded-lg bg-[#26395F] flex items-center justify-center mb-5">
                <p.icon className="h-5 w-5 text-[#3B82F6]" />
              </span>
              <h3 className="font-display text-lg tracking-tight uppercase">{p.title}</h3>
              <p className="text-sm text-[#475569] mt-2.5 leading-relaxed">{p.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Commitments */}
      <section className="bg-[#F5F7FC] border-y border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl tracking-tight uppercase text-[#0F172A]">Our commitments</h2>
            <p className="text-[#475569] mt-3">Simple promises about how we handle your money and information.</p>
          </div>
          <ul className="space-y-3">
            {COMMITMENTS.map((c) => (
              <li key={c} className="flex items-start gap-3 bg-white border border-[#E2E8F0] rounded-lg px-5 py-4">
                <CheckCircle2 className="h-5 w-5 text-[#3B82F6] shrink-0 mt-0.5" />
                <span className="text-sm text-[#0F172A]">{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Security Plans */}
      <section id="security-plans" className="bg-white border-t border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full bg-[#3B82F6]/10 border border-[#3B82F6]/25 px-4 py-1.5 text-xs font-semibold text-[#3B82F6]" data-testid="security-reseller-badge">
                  <ShieldCheck className="h-4 w-4" /> {RESELLER.text}
                </span>
                <a
                  href="/avs-reseller-certificate.pdf"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-sweep inline-flex items-center gap-2 rounded-full bg-[#3B82F6] text-white text-xs font-bold px-4 py-2 shadow-[0_4px_14px_rgba(59,130,246,0.35)] hover:bg-[#2563EB] transition-colors"
                  data-testid="security-reseller-cert-link"
                >
                  <FileCheck className="h-4 w-4" /> View our reseller certificate
                </a>
              </div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#3B82F6] mt-5">Genuine Antivirus Software</p>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight uppercase mt-3">Antivirus plans</h2>
              <p className="text-[#475569] mt-4 max-w-2xl">Genuine, licensed antivirus protection with free setup &amp; activation help — choose a device/term plan or pay a custom amount. One-time payment, no subscription.</p>
            </div>
            <img
              src="/reseller-seal.jpg"
              alt="Authorized Reseller since 2019 verified seal"
              className="h-32 w-32 sm:h-40 sm:w-40 rounded-full shrink-0 shadow-lg ring-4 ring-[#3B82F6]/10 self-start"
              data-testid="security-reseller-seal-img"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12 items-stretch">
            {SECURITY_PLANS.map((plan, i) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`relative rounded-2xl p-7 flex flex-col transition-all duration-300 ${
                  plan.featured ? "bg-[#26395F] text-white shadow-2xl" : "bg-white text-[#0F172A] border border-[#E2E8F0] hover:border-[#3B82F6] hover:shadow-lg"
                }`}
                data-testid={`security-plan-${plan.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-semibold uppercase tracking-[0.15em] ${plan.featured ? "text-white/60" : "text-[#475569]"}`}>{plan.name}</span>
                  {plan.badge && (
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${plan.featured ? "bg-[#3B82F6] text-[#0F172A]" : "bg-[#3B82F6]/10 text-[#3B82F6]"}`}>{plan.badge}</span>
                  )}
                </div>

                <div className="mt-5 flex items-end gap-2">
                  {plan.custom ? (
                    <span className="font-display text-4xl tracking-tight">Custom</span>
                  ) : (
                    <>
                      <span className="font-display text-5xl tracking-tight">${plan.price}</span>
                      <span className="mb-2 text-sm font-semibold text-[#3B82F6]">+ tax</span>
                    </>
                  )}
                </div>
                <p className={`text-xs mt-2 ${plan.featured ? "text-white/60" : "text-[#475569]"}`}>{plan.unit}</p>

                <ul className="mt-6 space-y-2.5 flex-1">
                  {plan.perks.map((perk) => (
                    <li key={perk} className={`flex items-start gap-2.5 text-sm ${plan.featured ? "text-white/85" : "text-[#334155]"}`}>
                      <Check className="h-4 w-4 text-[#3B82F6] shrink-0 mt-0.5" />
                      <span>{perk}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => goToCheckout(plan)}
                  className={`btn-sweep mt-7 w-full font-bold py-3 rounded-full transition-all duration-300 ${
                    plan.featured ? "bg-[#3B82F6] text-[#0F172A] hover:glow-primary" : "bg-[#26395F] text-white hover:bg-[#3B82F6] hover:text-[#0F172A]"
                  }`}
                  data-testid={`security-plan-select-${plan.id}`}
                >
                  {plan.custom ? "Enter your amount" : "Choose plan"}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="rounded-2xl bg-[#26395F] text-white p-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div>
            <h2 className="font-display text-2xl tracking-tight uppercase">Report a security concern</h2>
            <p className="text-white/70 mt-2 max-w-xl">Noticed something unusual or have a question about your payment security? Reach our team directly.</p>
          </div>
          <div className="flex flex-col gap-3 shrink-0">
            <a href="mailto:akarjagdev@gmail.com" className="inline-flex items-center gap-2.5 bg-[#3B82F6] text-[#0F172A] font-semibold px-6 py-3 rounded-full hover:opacity-90 transition-opacity" data-testid="security-email">
              <Mail className="h-4 w-4" /> akarjagdev@gmail.com
            </a>
            <a href="tel:+18644810776" className="inline-flex items-center gap-2.5 border border-white/25 text-white font-medium px-6 py-3 rounded-full hover:bg-white/10 transition-colors" data-testid="security-phone">
              <Phone className="h-4 w-4" /> (864) 481-0776
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-[#475569]">
          <span>© {new Date().getFullYear()} Radio App Consultancy. Independent Consultancy.</span>
          <div className="flex gap-6">
            <Link to="/terms" className="hover:text-[#0F172A]">Terms</Link>
            <Link to="/refund" className="hover:text-[#0F172A]">Refund Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
